import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Grid, Paper } from '@material-ui/core';
import { navigate } from '@gatsbyjs/reach-router/';
import { useGlobal, Typography } from '@rcg/ui-react';
import CheckIcon from '@material-ui/icons/CheckCircle';
import { globalizationPath } from '../../utils/globalization/globalizationPath';
import Dictionary from '../../utils/dictionary';
import Button from '../../components/UiComponents/Button';
import useStyles from './index.style';
import useDictionary from '../../hooks/useDictionary';
import {
  NAVIGATION_PATHS,
  SESSION_STORAGE_CURRENT_BOOKING,
  SESSION_STORAGE_BOOKING_PROFILE_NAME,
  SESSION_STORAGE_GDS_LOCATOR,
  COOKIE_KEYS,
} from '../../utils/constants';
import { getManageRedirectionUrl, getCookies } from '../../utils/common';
import { trackView } from '../../utils/analytics/bookFlightsTracking';

import { getSessionStorageItem } from '../../operations/sessionStorage/utils';
import NotificationBannerComponent from '../../components/NotificationBanner';
import useCommonStyles from '../../utils/useCommonStyles';

const CancelConfirmation = () => {
  const classes = useStyles();
  const styles = useCommonStyles();
  const {
    data: { lang, market, country },
  } = useGlobal();
  const getDictionaryValue = useDictionary(Dictionary);
  const [showTransferBanner, setShowTransferBanner] = useState(true);

  const profileBooking = getSessionStorageItem(SESSION_STORAGE_BOOKING_PROFILE_NAME);
  const { guests, currency } = profileBooking.flightProfile;
  const { sailing, id } = profileBooking.currentBooking;
  const currentBooking = getSessionStorageItem(SESSION_STORAGE_CURRENT_BOOKING);
  const transfersDetails =
    currentBooking?.flightProfile.guests[0].flightGuest.transfers ||
    guests[0].flightGuest.transfers;
  const locatorArray = getSessionStorageItem(SESSION_STORAGE_GDS_LOCATOR);

  const guestsByLocators = guests.filter((guest) =>
    guest.flightGuest.gdsLocators.some((locator) => locatorArray.includes(locator)),
  );
  const { guestFlights, airChargesAmount } = guestsByLocators[0].flightGuest || null;
  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);

  const formatConfirmationNumbers = () => {
    const confirmation = guestFlights.flatMap((item) => item.confirmationNumbers);
    const uniqueSet = new Set(confirmation);
    return [...uniqueSet].map((item) => item.replace(/-/g, '')).join(', ');
  };

  useEffect(() => {
    document.querySelector('.main-container').scrollTo(0, 0);
    const flightAirportCodes = guestFlights?.flatMap((flight) =>
      flight.flightSegments.flatMap((segment) => [
        segment.departure.airport.code,
        segment.arrival.airport.code,
      ]),
    );
    const loyaltyData = getCookies(COOKIE_KEYS.LOYALTY_DATA);

    const dataForTrackView = {
      pageName: `airbooking:cancelFlightConfirmation`,
      siteSection: 'airbooking',
      pageType: 'manageFlight',
      country,
      language: lang,
      loginStatus: loyaltyData ? 'Logged In' : 'Anonymous',
      currency,
      sailDate: sailing?.sailDate,
      ship: sailing?.itinerary?.ship?.code,
      bookingID: id,
      flightReservationNumber: formatConfirmationNumbers(),
      flightprodCategory: ['Flight'],
      flightprodSku: flightAirportCodes?.join('-'),
      flightprodQuantity: ['1'],
      flightProdCost: [`${airChargesAmount}`],
    };

    trackView(dataForTrackView);
  }, []);

  const goToReservation = () => {
    window.location.href = getManageRedirectionUrl();
  };

  const goToManage = () => {
    navigate(`${urlPath}${NAVIGATION_PATHS.manage}`, {
      state: { refetch: true },
    });
  };

  return (
    <>
      <Helmet>
        <title>{getDictionaryValue(Dictionary.airCancelConfirmationSeoTitle.key)}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Grid container className={classes.gridContainer}>
        <div className="notifications">
          {transfersDetails?.length > 0 && showTransferBanner && (
            <div className={styles.notification}>
              <NotificationBannerComponent
                title={getDictionaryValue(Dictionary.airConfirmationPageTransferBannerTitle.key)}
                description={`${getDictionaryValue(
                  Dictionary.airConfirmationPageTransferBannerDescription1.key,
                )} ${transfersDetails?.map((item) => `#${item.transferCode}`)} ${getDictionaryValue(
                  Dictionary.airConfirmationPageTransferBannerDescription2.key,
                )}`}
                type="info"
                renderCloseBtn
                isBannerOpen
                onClose={() => {
                  setShowTransferBanner(false);
                }}
              />
            </div>
          )}
        </div>
        <Grid item xs={12} className={classes.cancelConfirmationContainer}>
          <div className={classes.cancelConfirmationHeader}>
            <CheckIcon className={classes.checkIcon} />
            <Typography className={classes.confirmationTitle}>
              {getDictionaryValue(Dictionary.airCancelConfirmationTitle.key)}
            </Typography>
          </div>
          <Paper>
            <div className={classes.CancelConfirmationMessage}>
              {getDictionaryValue(Dictionary.airCancelConfirmationMessage1.key)}
              <span>{formatConfirmationNumbers()}</span>{' '}
              {getDictionaryValue(Dictionary.airCancelConfirmationMessage2.key)}
            </div>
            <div className={classes.CancelConfirmationCta}>
              <Button variant="outlined" color="primary" onClick={goToManage}>
                {getDictionaryValue(Dictionary.airConfirmationManageFlightsCTA.key)}
              </Button>
              <Button
                data-testid="go-to-cruise-reservation-btn"
                onClick={goToReservation}
                variant="contained"
                color="primary">
                {getDictionaryValue(Dictionary.airGoToCruiseReservationLabel.key)}
              </Button>
            </div>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default CancelConfirmation;
